import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import Button from '../components/ui/Button';
import AvailabilityCalendar from '../components/profile_page/AvailabilityCalendar';
import BioSection from '../components/profile_page/BioSection';
import ProfileImageContainer from '../components/profile_page/ProfileImageContainer';
import PortfolioSection from '../components/profile_page/PortfolioSection';
import EditProfileModal from '../components/profile_page/EditProfileModal';
import MessageButton from '../components/reusable/MessageButton';
import PackagesPanel from '../components/profile_page/PackagesPanel.js';
import TravelPanel from '../components/profile_page/TravelPanel';
import '../styles/pages/ProfilePage.css';
import { buildDisplayName, getShortName } from '../utils/nameFormatting';

const ProfilePage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, accountReady, userAttributes, authFetch } = useAuth();
  const { webSocketRef } = useWebSocket();

  const [userDetails, setUserDetails] = useState(null);
  const [profileBelongsToUser, setIsCurrentUserProfile] = useState(false);
  const isCurrentUserProfile = profileBelongsToUser && accountReady !== false;
  const [profileImageUrl, setProfileImageUrl] = useState('/images/default_profile_picture.png');
  const [coverPhotoUrl, setCoverPhotoUrl] = useState('/images/default_cover_photo.jpg');
  const [modalOpen, setModalOpen] = useState(false);
  const [editSection, setEditSection] = useState('');
  const [loadedProfileUsername, setLoadedProfileUsername] = useState(null);
  const [bio, setBio] = useState('');
  const [specialities, setSpecialities] = useState([]);

  // NEW STATE (profile_content driven)
  const [travelData, setTravelData] = useState({
    base_location: '',
    free_radius_km: 0,
    extra_travel_per_mile: 0,
    regions: [],
    notes: [],
  });

  const [packages, setPackages] = useState([]);

  
  // ========= WebSocket listener =========
  useEffect(() => {
    if (!userAttributes?.sub) return;

    const handleWebSocketMessage = (event) => {
      const data = JSON.parse(event.data);

      if (data.action === 'updateProfilePicture' && data.userSub === userAttributes.sub) {
        setProfileImageUrl(`${data.url}?${Date.now()}`);
      }

      if (data.action === 'updateCoverPhoto' && data.userSub === userAttributes.sub) {
        setCoverPhotoUrl(`${data.url}?${Date.now()}`);
      }
    };

    const currentWebSocket = webSocketRef.current;

    if (currentWebSocket) {
      currentWebSocket.addEventListener('message', handleWebSocketMessage);
    }

    return () =>
      currentWebSocket?.removeEventListener('message', handleWebSocketMessage);

  }, [webSocketRef, userAttributes?.sub]);

  // ========= Load profile basics + written content =========
  useEffect(() => {
    const loadProfile = async () => {
      setLoadedProfileUsername(null);
      try {
        const lookupResponse = await fetch(
          `https://0zr99zyhbk.execute-api.eu-west-2.amazonaws.com/dev/profiles/${username}`
        );

        const lookupData = await lookupResponse.json();

        if (lookupResponse.status === 404) {
          navigate('/profile-not-found');
          return;
        }

        if (!lookupResponse.ok) {
          throw new Error(`Profile lookup failed: ${lookupResponse.status}`);
        }

        setUserDetails(lookupData);

        const cacheBuster = Date.now();
        setProfileImageUrl(
          lookupData.profile_picture_s3_url
            ? `${lookupData.profile_picture_s3_url}?${cacheBuster}`
            : '/images/default_profile_picture.png'
        );

        setCoverPhotoUrl(
          lookupData.cover_photo_s3_url
            ? `${lookupData.cover_photo_s3_url}?${cacheBuster}`
            : '/images/default_cover_photo.jpg'
        );

        if (isAuthenticated && lookupData.id === userAttributes.sub) {
          setIsCurrentUserProfile(true);
        } else {
          setIsCurrentUserProfile(false);
        }

        const contentResponse = await fetch(
          `https://40n03951u0.execute-api.eu-west-2.amazonaws.com/dev/users/${lookupData.id}/profile-content`
        );

        if (!contentResponse.ok) {
          throw new Error(`Profile content fetch failed: ${contentResponse.status}`);
        }

        const profileContent = await contentResponse.json();

        setBio(profileContent.bio || '');

        const travel = profileContent.travel || {};
        setTravelData({
          base_location: travel.base_location || '',
          free_radius_km: travel.free_radius_km || 0,
          extra_travel_per_mile: travel.extra_travel_per_mile || 0,
          regions: travel.regions || [],
          notes: travel.notes || [],
        });

        setPackages(profileContent.packages || []);
        setLoadedProfileUsername(username);
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };

    loadProfile();
  }, [username, navigate, isAuthenticated, userAttributes]);

  // ========= Fetch specialities + portfolio =========
  const fetchSpecialities = useCallback(async () => {
    const userSub = isCurrentUserProfile ? userAttributes.sub : userDetails.id;
    try {
      const response = await fetch(
        `https://7ivi6888a9.execute-api.eu-west-2.amazonaws.com/dev/photographers/${userSub}/specialities`
      );
      const data = await response.json();
      if (response.ok) setSpecialities(data);
    } catch (e) {
      console.error('Error fetching specialities:', e);
    }
  }, [isCurrentUserProfile, userAttributes?.sub, userDetails]);

  useEffect(() => {
    if (userDetails) fetchSpecialities();
  }, [userDetails, fetchSpecialities]);



  // ========= Modal helpers =========
  useEffect(() => {
    if (searchParams.get('edit') !== 'specialities' || loadedProfileUsername !== username || !isAuthenticated || !isCurrentUserProfile
      || userDetails?.id !== userAttributes?.sub || userAttributes?.['custom:userRole'] !== 'photographer') return;
    setEditSection('specialities');
    setModalOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('edit');
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams, loadedProfileUsername, username, isAuthenticated, isCurrentUserProfile, userDetails?.id, userAttributes]);

  const handleOpenModal = () => {
    setEditSection('');
    setModalOpen(true);
  };

  const displayName = userDetails
    ? buildDisplayName(userDetails.first_name, userDetails.second_name)
    : '';
  const shortName = userDetails
    ? getShortName(userDetails.first_name)
    : '';

  return (
    <div className="profile-page">
      <section className="profile-hero">
        <div className="cover-photo-container">
          <img className="cover-photo" src={coverPhotoUrl} alt="Profile cover" />
        </div>

        <div className="profile-identity-row">
          <div className="profile-avatar-wrap">
            <ProfileImageContainer profileImageUrl={profileImageUrl} />
          </div>

          <div className="profile-identity-copy">
            {userDetails && (
              <h1 className="profile-name">
                {displayName}
              </h1>
            )}

            {specialities.length > 0 && (
              <p className="specialities-list">
                {specialities.map((item) => item.speciality).join(' · ')}
              </p>
            )}

            <BioSection bio={bio} isOwner={isCurrentUserProfile} onAddClick={handleOpenModal} />
          </div>

          <div className="profile-identity-actions">
            {!isCurrentUserProfile && userDetails && (
              <MessageButton
                className="message-profile-page-button"
                userSub={userDetails.id}
                recipientName={displayName}
              />
            )}

            {isCurrentUserProfile && (
              <Button
                variant="primary"
                size="md"
                onClick={handleOpenModal}
                className="edit-profile-btn"
              >
                Edit Profile
              </Button>
            )}
          </div>
        </div>
      </section>

      <div className="profile-content">
        <div className="portfolio-section">
          <PortfolioSection
            photographerId={userDetails?.id}
            isOwner={isCurrentUserProfile}
            authFetch={authFetch}
            userSub={userAttributes?.sub}
            firstName={shortName}
          />
        </div>

        <div className="availability-section">
          <div className="availability-row">

            <div className="availability-col availability-col--calendar profile-panel">
              <h2 className="availability-section__title">{isCurrentUserProfile ? 'Your Availability' : 'Availability'}</h2>
              <AvailabilityCalendar photographerId={userDetails?.id} />
            </div>

            <div className="availability-col">
              <TravelPanel travel={travelData} isOwner={isCurrentUserProfile} firstName={shortName} />
            </div>

            <div className="availability-col">
              <PackagesPanel
                isOwner={isCurrentUserProfile}
                firstName={shortName}
                packages={packages.map(pkg => ({
                  ...pkg,
                  onClick: (id) => console.log('enquire', id)
                }))}
              />
            </div>

          </div>
        </div>
      </div>

      {modalOpen && isCurrentUserProfile && (
        <EditProfileModal
          initialSection={editSection}
          onClose={() => setModalOpen(false)}
          profileImageUrl={profileImageUrl}
          coverPhotoUrl={coverPhotoUrl}
          bio={bio}
          setBio={setBio}
          userAttributes={userAttributes}
          setProfileImageUrl={setProfileImageUrl}
          setCoverPhotoUrl={setCoverPhotoUrl}
          travelData={travelData}
          packages={packages}
          setTravelData={setTravelData}
          setPackages={setPackages}
          setSpecialities={setSpecialities}
        />
      )}
    </div>
  );
};

export default ProfilePage;
